import axios from 'axios';

// Generic, feature-agnostic PDF download/print helpers used by any page
// that renders a server-rendered PDF (Quotes, Invoices, Customer
// Statements, ...). Originally lived in quotesInvoices/docHelpers.js;
// relocated here once a second, unrelated feature needed them too.

// Fetches the PDF as a blob (via axios, so the session cookie goes with it)
// and saves it through a same-window, no-navigation <a download> click.
//
// The previous implementation used a plain `<a href target="_blank">`
// straight at the PDF endpoint. In a browser tab that's a normal download,
// but inside an installed PWA (display: standalone) there's no tab to open
// a target="_blank" navigation into, and standalone web-app windows can't
// render a raw `application/pdf` response themselves - the navigation to
// non-HTML content gets abandoned and the standalone window falls back to
// its start_url, which is exactly the "bounces back to the homepage"
// symptom. Fetching as a blob and clicking a `download`-attributed link
// never navigates the window at all, so there's nothing for the PWA shell
// to fall back away from - it works identically in a browser tab and in
// standalone/installed mode.
export async function downloadPdf(url, filename) {
    const response = await axios.get(url, { responseType: 'blob' });
    const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
}

// Same as downloadPdf, for any file type (Excel, CSV, PDF). The filename
// comes from the server's Content-Disposition when it sends one, falling
// back to the given name. Use this for every download button so it works
// in a browser tab and in the installed PWA alike.
export async function downloadFile(url, fallbackName = 'download') {
    const response = await axios.get(url, { responseType: 'blob' });
    const disposition = response.headers['content-disposition'] || '';
    const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    const filename = match ? decodeURIComponent(match[1]) : fallbackName;
    const type = response.headers['content-type'] || 'application/octet-stream';
    const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type }));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
}

// Same root cause/fix as downloadPdf above, but for Print: loads the PDF
// into a hidden same-page iframe (never a new window/tab or top-level
// navigation) and calls the browser's native print dialog on it once
// loaded, then tears the iframe down.
export async function printPdf(url) {
    const response = await axios.get(url, { responseType: 'blob' });
    const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.src = blobUrl;
    document.body.appendChild(iframe);

    // iframe.onload is unreliable for PDF content specifically: Chromium's
    // built-in PDF viewer renders via a native plugin that doesn't always
    // fire the standard DOM load event an HTML document would - confirmed
    // directly via headless testing, where the load event never fired at
    // all for a blob: PDF and print() was consequently never called, so
    // clicking Print silently did nothing. Race onload against a fixed
    // delay (guarded so it only ever fires once) so Print still works even
    // when onload never comes.
    let triggered = false;
    const triggerPrint = () => {
        if (triggered) return;
        triggered = true;
        try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
        } finally {
            setTimeout(() => {
                document.body.removeChild(iframe);
                window.URL.revokeObjectURL(blobUrl);
            }, 60000);
        }
    };
    iframe.onload = triggerPrint;
    setTimeout(triggerPrint, 1000);
}
